import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCamera, FaCheckCircle, FaCog, FaFileUpload, FaHourglassHalf } from "react-icons/fa";
import Modal from "./Modal";
import {
  TAX_ID_CAMERA_ACCEPT,
  TAX_ID_FIELD_STEPS,
  TAX_ID_FILE_ACCEPT,
  TAX_ID_LENGTHS,
  TAX_ID_REQUEST_TIMEOUT_MS,
  TAX_ID_TYPES,
  buildTaxIdChangeRequest,
  fieldErrorKey,
  formatDayMonth,
  formatCurrentTaxId,
  maskTaxIdInput,
  prepareTaxIdFile,
  resolveTaxIdErrorCode,
  taxIdErrorKey,
  validateNewTaxId,
  validatePickedFile,
  validatePreparedFiles,
  canCompactOversizedFile,
  compactTaxIdFile,
  needsCompaction,
} from "../helpers/taxIdChangeHelper";
import "../styles/AddressEditModal.css";
import "../styles/TaxIdChangeModal.css";

const STEPS = ["value", "document", "confirm"];
const SLOT_COUNT = 2;
const EMPTY_SLOT = Object.freeze({ file: null, processing: false, error: null });

// Server says the uploaded files are unusable: forget their ids so the next try uploads them again.
const REUPLOAD_ERROR_CODES = new Set(["FILE_NOT_FOUND", "FILE_NOT_ON_MATTER", "FILE_TOO_LARGE"]);

export const VERIFYING_MESSAGE_KEYS = ["verifying.checking", "verifying.details", "verifying.almost"];
export const VERIFYING_MESSAGE_INTERVAL_MS = 3000;

// Errors after which sending again makes no sense: the modal only offers "Close".
const FINAL_ERROR_CODES = new Set(["FORBIDDEN", "NOT_FOUND", "REQUEST_PENDING", "RATE_LIMITED", "TIMEOUT"]);

const formatSize = (bytes) => (bytes >= 1000000
  ? `${(bytes / 1000000).toFixed(1).replace(".", ",")} MB`
  : `${Math.max(1, Math.round(bytes / 1000))} KB`);

const slotFileName = (index) => (index === 0 ? "documento-frente" : "documento-verso");

const createPreview = (file) => (file?.contentType?.startsWith("image/")
  && typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
  ? URL.createObjectURL(file.blob)
  : null);

const revokePreview = (url) => {
  if (url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(url);
};

const TaxIdChangeModal = ({
  isOpen,
  onClose,
  t,
  type = TAX_ID_TYPES.CPF,
  currentValue,
  matters = [],
  initialMatterId,
  submit,
  uploadFile,
  onResult,
  onErrorCode,
  prepareFile = prepareTaxIdFile,
  timeoutMs = TAX_ID_REQUEST_TIMEOUT_MS,
}) => {
  const [step, setStep] = useState("value");
  const [value, setValue] = useState("");
  const [valueTouched, setValueTouched] = useState(false);
  const [slots, setSlots] = useState(() => Array(SLOT_COUNT).fill(EMPTY_SLOT));
  const [filesTouched, setFilesTouched] = useState(false);
  const [matterId, setMatterId] = useState("");
  const [matterTouched, setMatterTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendPhase, setSendPhase] = useState(null);
  const [verifyingStep, setVerifyingStep] = useState(0);
  const [serverError, setServerError] = useState(null);
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const [result, setResult] = useState(null);
  const controllerRef = useRef(null);
  const previewsRef = useRef([]);
  // Prepared file -> { matterId, fileId } of a finished upload, so a retry doesn't upload it again.
  const uploadsRef = useRef(new Map());
  const valueInputRef = useRef(null);
  const headingRef = useRef(null);

  useEffect(() => {
    setVerifyingStep(0);
    if (sendPhase !== "verifying") return undefined;
    const timer = setInterval(() => {
      setVerifyingStep((current) => {
        const next = Math.min(current + 1, VERIFYING_MESSAGE_KEYS.length - 1);
        if (next === VERIFYING_MESSAGE_KEYS.length - 1) clearInterval(timer);
        return next;
      });
    }, VERIFYING_MESSAGE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [sendPhase]);

  const tr = useCallback(
    (key, params = {}) => Object.entries({ type, ...params })
      .reduce((text, [name, val]) => text.split(`{${name}}`).join(val), t(`taxIdChange.${key}`)),
    [t, type],
  );

  const showMatterPicker = matters.length > 1;

  useEffect(() => {
    if (!isOpen) return undefined;
    setStep("value");
    setValue("");
    setValueTouched(false);
    setSlots(Array(SLOT_COUNT).fill(EMPTY_SLOT));
    setFilesTouched(false);
    setMatterId(
      matters.length > 1
        ? (matters.some((matter) => matter.id === initialMatterId) ? initialMatterId : "")
        : matters[0]?.id || "",
    );
    setMatterTouched(false);
    setSending(false);
    setSendPhase(null);
    setServerError(null);
    setServerFieldErrors({});
    setResult(null);
    uploadsRef.current = new Map();
    return undefined;
    // Only reset when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => () => {
    controllerRef.current?.abort();
    previewsRef.current.forEach(revokePreview);
  }, []);

  // Move focus to the new step's heading so screen readers announce it.
  useEffect(() => {
    if (!isOpen) return;
    if (step === "value") valueInputRef.current?.focus();
    else headingRef.current?.focus();
  }, [step, isOpen]);

  const valueError = useMemo(() => validateNewTaxId(value, type, currentValue), [value, type, currentValue]);
  const digitsTyped = value.replace(/\D/g, "").length;
  const showValueError = serverFieldErrors.newValue
    || ((valueTouched || digitsTyped >= TAX_ID_LENGTHS[type]) && valueError ? tr(`errors.${valueError}`) : null);

  const preparedFiles = slots.map((slot) => slot.file).filter(Boolean);
  const anyProcessing = slots.some((slot) => slot.processing);
  const filesError = validatePreparedFiles(preparedFiles);
  const compacting = !anyProcessing && needsCompaction(preparedFiles);

  // An image over its limit: re-encode it smaller once before showing the error.
  useEffect(() => {
    if (!compacting) return;
    const targets = slots
      .map((slot, index) => (canCompactOversizedFile(slot.file) ? index : null))
      .filter((index) => index !== null);
    setSlots((prev) => prev.map((slot, i) => (targets.includes(i) ? { ...slot, processing: true } : slot)));
    targets.forEach(async (index) => {
      const original = slots[index].file;
      let next;
      try {
        next = await compactTaxIdFile(original, slotFileName(index), prepareFile);
      } catch (error) {
        next = { ...original, compact: true };
      }
      const preview = next.blob === original.blob ? original.preview : createPreview(next);
      if (preview && preview !== original.preview) previewsRef.current.push(preview);
      setSlots((prev) => prev.map((slot, i) => {
        if (i !== index || slot.file !== original) return i === index ? { ...slot, processing: false } : slot;
        if (preview !== original.preview) revokePreview(original.preview);
        return { file: { ...next, compact: true, preview }, processing: false, error: null };
      }));
    });
    // Runs whenever a new over-budget state appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compacting]);
  const matterError = showMatterPicker && !matterId ? "matterRequired" : null;

  const handleValueChange = (event) => {
    setValue(maskTaxIdInput(event.target.value, type));
    setServerFieldErrors((prev) => ({ ...prev, newValue: undefined }));
  };

  const updateSlot = (index, patch) => setSlots((prev) => prev.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)));

  const handlePick = async (index, event) => {
    const picked = event.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    // eslint-disable-next-line no-param-reassign
    event.target.value = "";
    if (!picked) return;
    setServerFieldErrors((prev) => ({ ...prev, files: undefined }));
    setServerError(null);
    const pickError = validatePickedFile(picked);
    if (pickError) {
      updateSlot(index, { error: pickError });
      return;
    }
    updateSlot(index, { processing: true, error: null });
    try {
      const prepared = await prepareFile(picked, slotFileName(index));
      const preview = createPreview(prepared);
      if (preview) previewsRef.current.push(preview);
      setSlots((prev) => prev.map((slot, i) => {
        if (i !== index) return slot;
        revokePreview(slot.file?.preview);
        return { file: { ...prepared, preview }, processing: false, error: null };
      }));
    } catch (error) {
      updateSlot(index, { processing: false, error: "imageDecode" });
    }
  };

  const removeSlot = (index) => {
    setSlots((prev) => prev.map((slot, i) => {
      if (i !== index) return slot;
      revokePreview(slot.file?.preview);
      return EMPTY_SLOT;
    }));
  };

  const goToDocument = (event) => {
    event?.preventDefault();
    setValueTouched(true);
    if (valueError) {
      valueInputRef.current?.focus();
      return;
    }
    setServerError(null);
    setStep("document");
  };

  const goToConfirm = (event) => {
    event?.preventDefault();
    setFilesTouched(true);
    setMatterTouched(true);
    if (filesError || matterError || anyProcessing || slots.some((slot) => slot.error)) return;
    setServerError(null);
    setStep("confirm");
  };

  const handleError = (error, timedOut) => {
    let code = resolveTaxIdErrorCode(error);
    if (timedOut) code = "TIMEOUT";
    else if (!code && error instanceof TypeError) code = "NETWORK";
    const key = code ? taxIdErrorKey({ code }) : "generic";

    if (code === "VALIDATION_ERROR" && error?.fieldErrors) {
      const mapped = Object.entries(error.fieldErrors).reduce((acc, [field, raw]) => {
        if (!raw) return acc;
        const known = fieldErrorKey(raw);
        return { ...acc, [field]: known ? tr(`errors.${known}`) : tr("errors.validation") };
      }, {});
      const firstField = Object.keys(mapped).find((field) => TAX_ID_FIELD_STEPS[field]);
      setServerFieldErrors(mapped);
      setServerError({ key, final: false });
      if (firstField) setStep(TAX_ID_FIELD_STEPS[firstField]);
      return;
    }
    if (REUPLOAD_ERROR_CODES.has(code)) uploadsRef.current = new Map();
    if (code === "PAYLOAD_TOO_LARGE" || code === "FILE_TOO_LARGE") {
      setServerError({ key, final: false });
      setStep("document");
      return;
    }
    setServerError({ key, final: FINAL_ERROR_CODES.has(code) });
    onErrorCode?.(code);
  };

  const uploadAll = async () => {
    const fileIds = [];
    // Sequential: keeps the progress honest on slow mobile connections and the ids in slot order.
    // eslint-disable-next-line no-restricted-syntax
    for (const file of preparedFiles) {
      const cached = uploadsRef.current.get(file);
      if (cached && cached.matterId === matterId) {
        fileIds.push(cached.fileId);
      } else {
        let fileId;
        try {
          // eslint-disable-next-line no-await-in-loop
          fileId = await uploadFile(file, matterId);
        } catch (error) {
          throw Object.assign(new Error("UPLOAD_FAILED"), { code: "UPLOAD_FAILED", cause: error });
        }
        if (!fileId) throw Object.assign(new Error("UPLOAD_FAILED"), { code: "UPLOAD_FAILED" });
        uploadsRef.current.set(file, { matterId, fileId });
        fileIds.push(fileId);
      }
    }
    return fileIds;
  };

  const handleSend = async () => {
    if (!matterId) {
      setMatterTouched(true);
      setStep("document");
      return;
    }
    setSending(true);
    setSendPhase("uploading");
    setServerError(null);
    setServerFieldErrors({});
    const controller = new AbortController();
    controllerRef.current = controller;
    let timedOut = false;
    let timer = null;
    try {
      const fileIds = await uploadAll();
      if (controllerRef.current !== controller) return;
      setSendPhase("verifying");
      timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs);
      const payload = buildTaxIdChangeRequest({ type, newValue: value, matterId, fileIds });
      const response = await submit(payload, { signal: controller.signal });
      if (controllerRef.current !== controller) return;
      const status = response?.status === "APPLIED" ? "APPLIED" : "UNDER_REVIEW";
      const finalResult = { ...response, status, type: response?.type || type };
      setResult(finalResult);
      setStep("result");
      onResult?.(finalResult);
    } catch (error) {
      if (controllerRef.current !== controller) return;
      handleError(error, timedOut);
    } finally {
      if (timer) clearTimeout(timer);
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setSending(false);
        setSendPhase(null);
      }
    }
  };

  const stepIndex = STEPS.indexOf(step);
  const titleId = "tax-id-change-title";
  const fileErrorText = filesError && !compacting && (filesTouched || filesError !== "fileRequired") ? filesError : null;

  const slotLabels = type === TAX_ID_TYPES.CNPJ
    ? [tr("document.cnpjMain"), tr("document.cnpjExtra")]
    : [tr("document.front"), tr("document.back")];

  const renderSlot = (slot, index) => {
    const label = slotLabels[index];
    const inputBase = `tax-id-file-${index}`;
    return (
      <div className="tax-id-slot" key={inputBase} data-testid={`tax-id-slot-${index}`}>
        <span className="tax-id-slot-label" id={`${inputBase}-label`}>
          {label}
          {index === 0 && <span className="address-edit-required" aria-hidden="true"> *</span>}
        </span>
        {slot.file ? (
          <div className="tax-id-slot-file">
            {slot.file.preview ? (
              <img className="tax-id-slot-thumb" src={slot.file.preview} alt="" />
            ) : (
              <FaFileUpload className="tax-id-slot-icon" aria-hidden="true" />
            )}
            <span className="tax-id-slot-name" data-testid={`tax-id-slot-name-${index}`}>
              {slot.file.fileName}
              <span className="tax-id-slot-size"> · {formatSize(slot.file.size)}</span>
            </span>
            <button
              type="button"
              className="portal-secondary-button tax-id-slot-remove"
              onClick={() => removeSlot(index)}
              aria-label={tr("document.removeAria", { name: slot.file.fileName })}
            >
              {tr("document.remove")}
            </button>
          </div>
        ) : slot.processing ? (
          <p className="tax-id-slot-processing" role="status">
            <FaCog className="spinner-cog" aria-hidden="true" />
            {tr("document.processing")}
          </p>
        ) : (
          <div className="tax-id-slot-actions" role="group" aria-labelledby={`${inputBase}-label`}>
            <input
              id={`${inputBase}-camera`}
              className="tax-id-visually-hidden tax-id-camera-input"
              type="file"
              accept={TAX_ID_CAMERA_ACCEPT}
              capture="environment"
              onChange={(event) => handlePick(index, event)}
              data-testid={`${inputBase}-camera`}
            />
            <label className="portal-secondary-button tax-id-pick tax-id-pick-camera" htmlFor={`${inputBase}-camera`}>
              <FaCamera aria-hidden="true" />
              {tr("document.takePhoto")}
            </label>
            <input
              id={`${inputBase}-upload`}
              className="tax-id-visually-hidden"
              type="file"
              accept={TAX_ID_FILE_ACCEPT}
              onChange={(event) => handlePick(index, event)}
              data-testid={`${inputBase}-upload`}
            />
            <label className="portal-secondary-button tax-id-pick" htmlFor={`${inputBase}-upload`}>
              <FaFileUpload aria-hidden="true" />
              {tr("document.chooseFile")}
            </label>
          </div>
        )}
        {slot.error && (
          <span className="address-edit-field-error" role="alert">{tr(`errors.${slot.error}`)}</span>
        )}
      </div>
    );
  };

  const selectedMatter = matters.find((matter) => matter.id === matterId);
  const canClose = !sending;
  const progressKey = sendPhase === "verifying" ? VERIFYING_MESSAGE_KEYS[verifyingStep] : "uploading";

  return (
    <Modal
      isOpen={isOpen}
      onClose={canClose ? onClose : () => {}}
      trapFocus
      labelledBy={titleId}
      closeLabel={tr("closeAria")}
    >
      <div className="address-edit-modal tax-id-modal" data-testid="tax-id-change-modal" aria-busy={sending}>
        <h3 className="address-edit-title" id={titleId}>
          {tr("modalTitle")}
        </h3>

        {step !== "result" && (
          <ol className="tax-id-steps" aria-label={tr("stepIndicator", { current: stepIndex + 1, total: STEPS.length })}>
            {STEPS.map((name, index) => (
              <li
                key={name}
                className={`tax-id-step${index === stepIndex ? " is-current" : ""}${index < stepIndex ? " is-done" : ""}`}
                aria-current={index === stepIndex ? "step" : undefined}
              >
                <span className="tax-id-step-number" aria-hidden="true">{index + 1}</span>
                <span className="tax-id-step-name">{tr(`steps.${name}`)}</span>
              </li>
            ))}
          </ol>
        )}

        {serverError && step !== "result" && (
          <div className="address-edit-banner is-error" role="alert" data-testid="tax-id-error">
            {tr(`errors.${serverError.key}`)}
          </div>
        )}

        {step === "value" && (
          <form className="tax-id-form" onSubmit={goToDocument} noValidate>
            <div className="tax-id-current">
              <span className="tax-id-current-label">{tr("currentLabel")}</span>
              <span className="tax-id-current-value" data-testid="tax-id-current">{formatCurrentTaxId(currentValue, type)}</span>
            </div>
            <div className="address-edit-field tax-id-value-field">
              <label htmlFor="tax-id-new-value">
                {tr("newLabel")}
                <span className="address-edit-required" aria-hidden="true"> *</span>
              </label>
              <input
                ref={valueInputRef}
                id="tax-id-new-value"
                className={`portal-input address-edit-input tax-id-value-input is-${type === TAX_ID_TYPES.CNPJ ? "cnpj" : "cpf"}${
                  showValueError ? " input-error" : ""
                }`}
                value={value}
                onChange={handleValueChange}
                onBlur={() => setValueTouched(true)}
                inputMode="numeric"
                autoComplete="off"
                placeholder={type === TAX_ID_TYPES.CNPJ ? "00.000.000/0000-00" : "000.000.000-00"}
                maxLength={type === TAX_ID_TYPES.CNPJ ? 18 : 14}
                aria-required="true"
                aria-invalid={Boolean(showValueError)}
                aria-describedby={showValueError ? "tax-id-new-value-error" : "tax-id-new-value-hint"}
                data-autofocus
              />
              {showValueError ? (
                <span className="address-edit-field-error" id="tax-id-new-value-error" role="alert">{showValueError}</span>
              ) : !valueError ? (
                <span className="address-edit-cep-status is-success" id="tax-id-new-value-hint" role="status">
                  {tr("valid")}
                </span>
              ) : (
                <span className="address-edit-hint" id="tax-id-new-value-hint">{tr("valueHint")}</span>
              )}
            </div>
            <div className="address-edit-actions">
              <button type="button" className="portal-secondary-button" onClick={onClose}>
                {tr("cancel")}
              </button>
              <button type="submit" className="portal-button" disabled={Boolean(valueError) && valueTouched}>
                {tr("next")}
              </button>
            </div>
          </form>
        )}

        {step === "document" && (
          <form className="tax-id-form" onSubmit={goToConfirm} noValidate>
            <h4 className="tax-id-section-title" tabIndex={-1} ref={headingRef}>
              {type === TAX_ID_TYPES.CNPJ ? tr("document.cnpjTitle") : tr("document.idTitle")}
            </h4>
            <p className="address-edit-hint">
              {type === TAX_ID_TYPES.CNPJ ? tr("document.cnpjHint") : tr("document.idHint")}
            </p>
            <div className="tax-id-tips" aria-label={tr("document.tipsTitle")}>
              <strong>{tr("document.tipsTitle")}</strong>
              <ul>
                <li>{tr("document.tipSharp")}</li>
                <li>{tr("document.tipWhole")}</li>
                <li>{tr("document.tipFormats")}</li>
              </ul>
            </div>
            <div className="tax-id-slots">{slots.map(renderSlot)}</div>
            {(fileErrorText || serverFieldErrors.files) && (
              <span className="address-edit-field-error" role="alert" data-testid="tax-id-files-error">
                {serverFieldErrors.files || tr(`errors.${fileErrorText}`)}
              </span>
            )}

            {showMatterPicker && (
              <div className="address-edit-field">
                <label htmlFor="tax-id-matter">
                  {tr("matterLabel")}
                  <span className="address-edit-required" aria-hidden="true"> *</span>
                </label>
                <span className="address-edit-hint" id="tax-id-matter-hint">{tr("matterHint")}</span>
                <select
                  id="tax-id-matter"
                  className={`portal-select address-edit-input address-edit-select${matterId ? "" : " is-placeholder"}${
                    (matterTouched && matterError) || serverFieldErrors.matterId ? " input-error" : ""}`}
                  value={matterId}
                  onChange={(event) => {
                    setMatterId(event.target.value);
                    setServerFieldErrors((prev) => ({ ...prev, matterId: undefined }));
                  }}
                  onBlur={() => setMatterTouched(true)}
                  aria-required="true"
                  aria-invalid={Boolean((matterTouched && matterError) || serverFieldErrors.matterId)}
                  aria-describedby="tax-id-matter-hint"
                >
                  <option value="">{tr("matterPlaceholder")}</option>
                  {matters.map((matter) => (
                    <option key={matter.id} value={matter.id}>{matter.displayName || matter.name || matter.id}</option>
                  ))}
                </select>
                {((matterTouched && matterError) || serverFieldErrors.matterId) && (
                  <span className="address-edit-field-error" role="alert">
                    {serverFieldErrors.matterId || tr("errors.matterRequired")}
                  </span>
                )}
              </div>
            )}

            <div className="address-edit-actions">
              <button type="button" className="portal-secondary-button" onClick={() => setStep("value")}>
                {tr("back")}
              </button>
              <button type="submit" className="portal-button" disabled={anyProcessing}>
                {tr("next")}
              </button>
            </div>
          </form>
        )}

        {step === "confirm" && (
          <div className="tax-id-form">
            <h4 className="tax-id-section-title" tabIndex={-1} ref={headingRef}>{tr("confirm.title")}</h4>
            <dl className="tax-id-summary">
              <div><dt>{tr("confirm.type")}</dt><dd>{type}</dd></div>
              <div><dt>{tr("confirm.current")}</dt><dd>{formatCurrentTaxId(currentValue, type)}</dd></div>
              <div><dt>{tr("confirm.new")}</dt><dd data-testid="tax-id-confirm-new"><strong>{value}</strong></dd></div>
              <div>
                <dt>{tr("confirm.files")}</dt>
                <dd>{preparedFiles.map((file) => file.fileName).join(", ")}</dd>
              </div>
              {showMatterPicker && selectedMatter && (
                <div>
                  <dt>{tr("confirm.matter")}</dt>
                  <dd>{selectedMatter.displayName || selectedMatter.name}</dd>
                </div>
              )}
            </dl>
            <p className="address-edit-hint">{tr("confirm.note")}</p>

            {sending && (
              <div className="tax-id-sending" role="status" aria-live="polite" data-testid="tax-id-sending">
                <FaCog className="spinner-cog" aria-hidden="true" />
                <div>
                  <strong
                    key={progressKey}
                    className="tax-id-sending-message"
                    data-testid="tax-id-sending-phase"
                    data-phase={sendPhase || "uploading"}
                  >
                    {tr(progressKey)}
                  </strong>
                  <p className="address-edit-hint">{tr("progressHint")}</p>
                </div>
              </div>
            )}

            <div className="address-edit-actions">
              {serverError?.final ? (
                <button type="button" className="portal-button" onClick={onClose}>{tr("close")}</button>
              ) : (
                <>
                  <button
                    type="button"
                    className="portal-secondary-button"
                    onClick={() => setStep("document")}
                    disabled={sending}
                  >
                    {tr("back")}
                  </button>
                  <button type="button" className="portal-button" onClick={handleSend} disabled={sending}>
                    {sending ? tr(progressKey) : serverError ? tr("retry") : tr("send")}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {step === "result" && result && (
          <div className="tax-id-result" data-testid="tax-id-result" data-status={result.status}>
            {result.status === "APPLIED" ? (
              <>
                <FaCheckCircle className="tax-id-result-icon is-success" aria-hidden="true" />
                <h4 className="tax-id-result-title" tabIndex={-1} ref={headingRef}>
                  {tr("result.appliedTitle", { type: result.type })}
                </h4>
                <p>{tr("result.appliedBody")}</p>
              </>
            ) : (
              <>
                <FaHourglassHalf className="tax-id-result-icon is-review" aria-hidden="true" />
                <h4 className="tax-id-result-title" tabIndex={-1} ref={headingRef}>{tr("result.reviewTitle")}</h4>
                <p>
                  {formatDayMonth(result.dueDate)
                    ? tr("result.reviewBody", { date: formatDayMonth(result.dueDate) })
                    : tr("result.reviewBodyNoDate")}
                </p>
              </>
            )}
            <div className="address-edit-actions">
              <button type="button" className="portal-button" onClick={onClose}>{tr("close")}</button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TaxIdChangeModal;
